// Humanized labels for a follow-up's proposed / chosen
// channel. Shared by the call-detail / after-call follow-up
// section (`CallFollowUps`) and the inbox summary's suggested
// next-step row (`SuggestedNextStepRow`, CON-204) so the two
// surfaces never drift on how a method reads ("WhatsApp" /
// "Call" / …).

import { msg } from "@lingui/core/macro"
import type { MessageDescriptor, I18n } from "@lingui/core"

const METHOD_LABELS: Record<string, MessageDescriptor> = {
  voice_call_outbound: msg`Call`,
  voice_call_inbound: msg`Call`,
  voice_call: msg`Call`,
  // WhatsApp and SMS stay verbatim — translator will leave them unchanged.
  whatsapp_message: msg`WhatsApp`,
  whatsapp_call: msg`WhatsApp`,
  email: msg`Email`,
  email_inbound: msg`Email`,
  email_outbound: msg`Email`,
  sms: msg`SMS`,
  sms_inbound: msg`SMS`,
  sms_outbound: msg`SMS`,
}

// Maps a raw method key to its human label, falling back to the
// raw key for anything unmapped. `null` in → `null` out so
// callers can drop the metadata cleanly.
// Accepts an `i18n` instance (from `useLingui()`) to resolve
// the descriptor to a string at render time.
export function methodLabel(
  method: string | null,
  i18n: I18n,
): string | null {
  if (!method) return null
  const descriptor = METHOD_LABELS[method]
  if (descriptor) return i18n._(descriptor)
  return method
}

// Methods offered by the "+ Add follow-up" composers (the
// call-detail panel and the inbox rail's NEXT STEPS). A
// curated subset of Activity's method enum — outbound,
// rep-initiated channels only.
export const COMPOSER_METHODS: ReadonlyArray<{
  value: string
  label: MessageDescriptor
}> = [
  { value: "voice_call_outbound", label: msg`Call` },
  { value: "whatsapp_message", label: msg`WhatsApp` },
  { value: "email", label: msg`Email` },
  { value: "sms", label: msg`SMS` },
]

// Tomorrow at 09:00 local → ISO. The default schedule time
// for a fresh follow-up.
export function tomorrowAt9Iso(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}

const pad2 = (n: number) => String(n).padStart(2, "0")

// ISO → the local-time string a datetime-local input wants.
export function isoToLocalInput(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  )
}

// datetime-local string → ISO; invalid input falls back to
// tomorrow 9:00 so a submit never carries garbage.
export function localInputToIso(local: string): string {
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? tomorrowAt9Iso() : d.toISOString()
}
